import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const [state, setState] = useState({
        status: "checking", // checking | paid | expired | error
        attempts: 0,
    });
    const triggeredRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", attempts: 0 });
            return;
        }
        if (triggeredRef.current) return;
        triggeredRef.current = true;

        let cancelled = false;

        const poll = async (attempt) => {
            if (cancelled) return;
            if (attempt >= MAX_ATTEMPTS) {
                setState({ status: "error", attempts: attempt });
                return;
            }
            try {
                const res = await axios.get(
                    `${API}/checkout/status/${sessionId}`,
                );
                const data = res.data;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", attempts: attempt });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", attempts: attempt });
                    return;
                }
                setState({ status: "checking", attempts: attempt });
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
            } catch (err) {
                setState({ status: "error", attempts: attempt });
            }
        };

        poll(0);

        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div
            className="min-h-[70vh] flex items-center py-20"
            data-testid="payment-success-page"
        >
            <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="rounded-2xl p-10 bg-white border border-[#E3DCC8] shadow-sm text-center">
                    {state.status === "checking" && (
                        <div data-testid="payment-status-checking">
                            <Loader2 className="w-12 h-12 text-[#8C9C88] animate-spin mx-auto" />
                            <h1 className="mt-6 font-heading text-2xl text-[#2C332A]">
                                Confirming your payment…
                            </h1>
                            <p className="mt-3 text-[#5B6358]">
                                Just a moment while we finalize things with
                                Stripe.
                            </p>
                        </div>
                    )}

                    {state.status === "paid" && (
                        <div data-testid="payment-status-paid">
                            <CheckCircle2 className="w-14 h-14 text-[#8C9C88] mx-auto" />
                            <h1 className="mt-6 font-heading text-3xl text-[#2C332A]">
                                Payment confirmed.
                            </h1>
                            <p className="mt-4 text-[#5B6358] leading-relaxed">
                                Thank you for booking your 50-minute
                                consultation. Ray will reach out shortly to
                                schedule. If you'd like, you can also call
                                her directly.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    href="tel:4052540805"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8C9C88] text-white px-6 py-3 font-medium transition-all duration-300 hover:bg-[#7A8A76]"
                                    data-testid="success-call-ray"
                                >
                                    Call Ray: 405-254-0805
                                </a>
                                <Link
                                    to="/"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#8C9C88] text-[#8C9C88] px-6 py-3 font-medium transition-all duration-300 hover:bg-[#8C9C88] hover:text-white"
                                    data-testid="success-home-link"
                                >
                                    Back to Home
                                </Link>
                            </div>
                        </div>
                    )}

                    {state.status === "expired" && (
                        <div data-testid="payment-status-expired">
                            <XCircle className="w-12 h-12 text-[#B5A38E] mx-auto" />
                            <h1 className="mt-6 font-heading text-2xl text-[#2C332A]">
                                Checkout session expired
                            </h1>
                            <p className="mt-3 text-[#5B6358]">
                                No worries — you can try again anytime.
                            </p>
                            <Link
                                to="/consultation"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#8C9C88] text-white px-6 py-3 font-medium transition-all duration-300 hover:bg-[#7A8A76]"
                                data-testid="expired-retry-link"
                            >
                                Try again
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    )}

                    {state.status === "error" && (
                        <div data-testid="payment-status-error">
                            <XCircle className="w-12 h-12 text-[#B5A38E] mx-auto" />
                            <h1 className="mt-6 font-heading text-2xl text-[#2C332A]">
                                We couldn't confirm the payment
                            </h1>
                            <p className="mt-3 text-[#5B6358] leading-relaxed">
                                If you were charged, please call Ray directly
                                and we'll sort it out right away.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    href="tel:4052540805"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8C9C88] text-white px-6 py-3 font-medium transition-all duration-300 hover:bg-[#7A8A76]"
                                    data-testid="error-call-ray"
                                >
                                    Call 405-254-0805
                                </a>
                                <Link
                                    to="/consultation"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#8C9C88] text-[#8C9C88] px-6 py-3 font-medium transition-all duration-300 hover:bg-[#8C9C88] hover:text-white"
                                    data-testid="error-retry-link"
                                >
                                    Back to Consultation
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
